import React from 'react';
import { StyleSheet, Text, View, Image } from 'react-native';

import { billStatementFragment } from 'apollo/queries/autogenerate/operations';
import { PublicBillStatementQuery } from 'apollo/queries/autogenerate/operations-public';
import {
  PublicUnpaidBillStatus,
  PublicUnpaidBillStatement,
} from 'apollo/queries/autogenerate/schemas-public';
import { formatYen } from 'screens/StatementScreen/utils';
import colors from 'shared/constants/colors';
import isHighPriceBill from 'shared/modules/bill/utils/is-high-price-bill';
import { getNumberOfDaysToNow } from 'shared/utils/date';
import testID from 'utils/test-id';

type Props = {
  bills: PublicUnpaidBillStatement[];
  theOldestUnpaidBill?: PublicBillStatementQuery;
};

export default function PendingBillCard({ bills, theOldestUnpaidBill }: Props) {
  const openWebpage = (url: string) => window.open(url, '_blank');
  const billsCount = bills.length;

  const getText = () => {
    if (!theOldestUnpaidBill) {
      return null;
    }

    const oldestBill = theOldestUnpaidBill;

    if (
      oldestBill &&
      getNumberOfDaysToNow(oldestBill.billStatement.dueAt) > 120 &&
      !oldestBill.billStatement.combiniSettlementAvailable &&
      oldestBill.billStatement.bankTransferSettlementAvailable
    ) {
      return (
        <Text style={styles.explanationText}>
          お支払い期日を120日以上経過したご請求は、銀行振込でお支払いください。
          {'\n\n'}
          ご不明な点は、
          <Text
            style={styles.explanationTextLink}
            {...testID('link')}
            onPress={() => openWebpage(process.env.HELP_URL)}
          >
            お問い合わせフォーム
          </Text>
          からご連絡ください。
        </Text>
      );
    }

    return (
      <Text style={styles.explanationText}>
        {isHighPriceBill({
          outstandingAmount: oldestBill.billStatement.outstandingAmount,
        } as billStatementFragment)
          ? 'ご請求金額が299,000円を超えているため、コンビニ払いはご利用いただけません。お手数ですが、銀行振込にてお支払いください。\n\n'
          : ''}
        {bills.length !== 1 &&
        !isHighPriceBill({
          outstandingAmount: oldestBill.billStatement.outstandingAmount,
        } as billStatementFragment)
          ? 'アプリのすぐ払い機能を利用すると、お支払い待ちのご請求を一度にお支払い可能です。個別にご請求をお支払いいただく場合は、先に確定したご請求からお支払いください。\n\n'
          : ''}
        お支払いが遅れると、153円遅延手数料が加算される場合がございます。お早めにお支払いください。
      </Text>
    );
  };

  return (
    <View style={styles.container}>
      <View style={styles.titleContainer}>
        <Image
          source={require('shared/assets/images/icons/exclamation-white.png')}
          style={styles.icon}
        />
        <Text style={styles.title}>お支払い待ちのご請求があります</Text>
      </View>
      <View style={styles.infoContainer}>
        {bills.map((bill, index) => {
          const billingDate = new Date(bill.dueAt);
          const isOverdue = bill.status === PublicUnpaidBillStatus.OVERDUE;

          return (
            <View
              key={bill.id}
              style={[
                styles.billContainer,
                index === billsCount - 1 && styles.billContainerLast,
              ]}
            >
              <Text
                style={[
                  styles.billInfoText,
                  isOverdue && styles.billInfoTextOverdue,
                ]}
              >
                {`${billingDate.getFullYear()}年${
                  billingDate.getMonth() + 1
                }月分のご請求`}
              </Text>
              <Text
                style={[
                  styles.billInfoText,
                  isOverdue && styles.billInfoTextOverdue,
                ]}
              >
                {formatYen(bill.outstandingAmount)}
              </Text>
            </View>
          );
        })}
        <View style={styles.separator} />
        {getText()}
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    borderRadius: 16,
  },
  titleContainer: {
    height: 56,
    borderTopRightRadius: 16,
    borderTopLeftRadius: 16,
    backgroundColor: colors.alertRed,
    flexDirection: 'row',
    alignItems: 'center',
  },
  infoContainer: {
    padding: 24,
    backgroundColor: colors.white,
    borderBottomRightRadius: 16,
    borderBottomLeftRadius: 16,
  },
  billContainer: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginBottom: 16,
  },
  billContainerLast: {
    marginBottom: 24,
  },
  billInfoText: {
    fontFamily: 'Hiragino Sans',
    fontSize: 14,
    color: colors.blackPaidy,
    fontWeight: '600',
  },
  billInfoTextOverdue: {
    color: colors.alertRed,
  },
  separator: {
    height: 1,
    backgroundColor: colors.whiteOff,
  },
  explanationText: {
    marginTop: 24,
    fontFamily: 'Hiragino Sans',
    fontSize: 14,
    color: colors.blackPaidy,
    fontWeight: '300',
  },
  explanationTextLink: {
    textDecorationLine: 'underline',
    color: colors.pink,
  },
  icon: {
    width: 24,
    height: 24,
    marginLeft: 24,
    marginRight: 16,
  },
  title: {
    fontFamily: 'Hiragino Sans',
    fontSize: 14,
    color: colors.white,
    fontWeight: '600',
  },
});
